<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { ToggleGroupItemProps as RekaToggleGroupItemProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cva } from 'class-variance-authority'

// `mode` is set internally from the parent group's `preset` (see ToggleGroupItem
// template) — consumers don't pass it. Per-item colours go through the `color` prop;
// one-off colours can still use the marker contract (in ToggleGroup.vue) via `class`.
//
// Important (`!`) modifiers on selected text colors are required: Forgd's custom
// tokens (text-secondary, text-primal) are generated later in Tailwind's stylesheet
// than `text-white`/`text-primal`, so without `!` the cascade picks the wrong winner.
export const toggleItemVariants = cva(
  'inline-flex items-center justify-center ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 gap-2',
  {
    variants: {
      mode: {
        // No preset means no marker, so the selected item paints the marker look itself.
        default:
          'h-full rounded-md border border-transparent px-3 text-xs font-semibold bg-transparent text-secondary hover:bg-tertiary hover:text-primal [--marker-bg:var(--background-color-solid)] [--marker-border:transparent] data-[state=on]:bg-(--marker-bg) data-[state=on]:border-(--marker-border) data-[state=on]:text-white! disabled:opacity-50',
        outline:
          'rounded-md text-sm font-medium border border-input bg-transparent hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-accent data-[state=on]:text-accent-foreground disabled:opacity-50',
        // Forgd switch presets — items carry only text color; marker provides bg.
        switch:
          'relative z-10 h-full rounded-md text-xs font-semibold text-secondary data-[state=on]:text-white! disabled:cursor-not-allowed disabled:text-neutral-900/30! disabled:data-[state=on]:text-neutral-900/30! px-3',
        'switch-light':
          'relative z-10 h-full rounded-md text-xs font-semibold text-secondary data-[state=on]:text-primal! disabled:cursor-not-allowed disabled:text-neutral-900/30! disabled:data-[state=on]:text-neutral-900/30! px-3',
      },
      // Selected-state colour on top of the group preset. Feeds the marker through the
      // --marker-bg / --marker-border contract read in ToggleGroup.vue.
      color: {
        none: '',
        red: 'data-[state=on]:text-red-700! data-[state=on]:[--marker-bg:var(--color-red-100)] data-[state=on]:[--marker-border:color-mix(in_oklab,var(--color-red-600)_30%,transparent)]',
        yellow:
          'data-[state=on]:text-yellow-800! data-[state=on]:[--marker-bg:var(--color-yellow-100)] data-[state=on]:[--marker-border:color-mix(in_oklab,var(--color-yellow-700)_30%,transparent)]',
        green:
          'data-[state=on]:text-green-700! data-[state=on]:[--marker-bg:var(--color-green-100)] data-[state=on]:[--marker-border:color-mix(in_oklab,var(--color-green-600)_30%,transparent)]',
        emerald:
          'data-[state=on]:text-white! data-[state=on]:[--marker-bg:var(--color-green-600)] data-[state=on]:[--marker-border:transparent]',
        brand:
          'data-[state=on]:text-white! data-[state=on]:[--marker-bg:var(--color-brand-300)] data-[state=on]:[--marker-border:transparent]',
      },
    },
    defaultVariants: {
      mode: 'default',
      color: 'none',
    },
  },
)

export type ToggleItemVariants = VariantProps<typeof toggleItemVariants>

export type ToggleGroupItemProps = RekaToggleGroupItemProps & {
  class?: HTMLAttributes['class']
  color?: ToggleItemVariants['color']
}
</script>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import { ToggleGroupItem, useForwardProps } from 'reka-ui'
import { computed } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { injectToggleGroupContext } from './ToggleGroup.vue'

const props = defineProps<ToggleGroupItemProps>()

const context = injectToggleGroupContext(null)

const PRESET_TO_MODE = { dark: 'switch', light: 'switch-light' } as const

const mode = computed<ToggleItemVariants['mode']>(() => {
  return PRESET_TO_MODE[context?.preset as keyof typeof PRESET_TO_MODE] ?? 'default'
})

const delegatedProps = reactiveOmit(props, 'class', 'color')
const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
  <ToggleGroupItem
    v-slot="slotProps"
    v-bind="forwardedProps"
    :class="cn(toggleItemVariants({ mode, color }), props.class)"
  >
    <slot v-bind="slotProps" />
  </ToggleGroupItem>
</template>
